import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, X, Link, Square, Circle } from 'lucide-react';
import { getCroppedCanvas, canvasToBlob } from './canvasUtils';

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 80,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}


function padWithWhite(dataUrl: string, ratio = 0.4): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Use naturalWidth/naturalHeight — img.width is 0 for detached Image() elements
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) {
        reject(new Error('Could not read image dimensions'));
        return;
      }
      const px = Math.round(w * ratio);
      const py = Math.round(h * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w + px * 2;
      canvas.height = h + py * 2;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, px, py, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load image for padding'));
    img.src = dataUrl;
  });
}

export interface ImageUploadWithCropProps {
  value: string;
  onChange: (url: string) => void;
  onUpload: (file: Blob) => Promise<string>;
  aspect?: number;
  shape?: 'rect' | 'round';
  paddingRatio?: number;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  allowUrlInput?: boolean;
}

export function ImageUploadWithCrop({
  value,
  onChange,
  onUpload,
  aspect = 16 / 9,
  shape = 'rect',
  paddingRatio = 0.4,
  placeholder = 'Upload image',
  disabled = false,
  error = false,
  className = '',
  allowUrlInput = false,
}: ImageUploadWithCropProps) {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imgDimensions, setImgDimensions] = useState<{ w: number; h: number } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInputValue, setUrlInputValue] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  // currentShape is runtime state — user can toggle inside the modal
  const [currentShape, setCurrentShape] = useState<'rect' | 'round'>(shape);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget;
      setImgDimensions({ w: naturalWidth, h: naturalHeight });
      // Round: start with a 1:1 square (enforced for true circle)
      // Rect: start with the caller's aspect hint, but user can resize freely from there
      if (currentShape === 'round') {
        setCrop(centerAspectCrop(naturalWidth, naturalHeight, 1));
      } else {
        setCrop(centerAspectCrop(naturalWidth, naturalHeight, aspect));
      }
    },
    [currentShape, aspect]
  );

  const handleShapeChange = (newShape: 'rect' | 'round') => {
    setCurrentShape(newShape);
    setCompletedCrop(undefined);
    if (imgDimensions) {
      if (newShape === 'round') {
        setCrop(centerAspectCrop(imgDimensions.w, imgDimensions.h, 1));
      } else {
        setCrop(centerAspectCrop(imgDimensions.w, imgDimensions.h, aspect));
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setCropError(null);
    setCurrentShape(shape);
    const reader = new FileReader();
    reader.addEventListener('load', async () => {
      try {
        const raw = reader.result?.toString() || '';
        const padded = await padWithWhite(raw, paddingRatio);
        setImgSrc(padded);
        setCompletedCrop(undefined);
      } catch {
        setCropError('Failed to prepare image. Please try again.');
      }
    });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleApplyCrop = async () => {
    if (!completedCrop?.width || !completedCrop?.height || !imgRef.current) {
      setCropError('Please adjust the crop area');
      return;
    }
    try {
      setIsUploading(true);
      setCropError(null);
      const canvas = getCroppedCanvas(imgRef.current, completedCrop, 1, 0, currentShape);
      const blob = await canvasToBlob(canvas);
      const url = await onUpload(blob);
      onChange(url);
      setImgSrc(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setImgDimensions(null);
    } catch (err) {
      setCropError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelCrop = () => {
    setImgSrc(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setImgDimensions(null);
    setCropError(null);
  };

  const handleRemove = () => {
    onChange('');
    setUrlInputValue('');
    setUrlError(null);
    setShowUrlInput(false);
  };

  const handleUrlApply = () => {
    const trimmed = urlInputValue.trim();
    if (!trimmed) return;
    setUrlError(null);
    setIsValidatingUrl(true);
    const img = new Image();
    img.onload = () => {
      setIsValidatingUrl(false);
      onChange(trimmed);
      setShowUrlInput(false);
      setUrlInputValue('');
    };
    img.onerror = () => {
      setIsValidatingUrl(false);
      setUrlError('This image URL could not be loaded. Please check the link and try again.');
    };
    img.src = trimmed;
  };

  // Aspect passed to ReactCrop: only for round (locks to 1:1 for a true circle),
  // rect is free-form (no aspect lock)
  const reactCropAspect = currentShape === 'round' ? 1 : undefined;

  const baseClass = `flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg transition-colors ${
    error ? 'border-red-500 bg-red-50/50' : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`;

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {value ? (
        <div className="relative inline-block bg-white rounded-lg border border-gray-200 p-2">
          <img
            src={value}
            alt="Preview"
            className="max-h-32 w-auto object-contain rounded-lg"
          />
          {!disabled && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
              >
                Change
              </button>
              {allowUrlInput && (
                <button
                  type="button"
                  onClick={() => setShowUrlInput(true)}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
                >
                  <Link className="w-3.5 h-3.5" />
                  URL
                </button>
              )}
              <button
                type="button"
                onClick={handleRemove}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div
            role="button"
            tabIndex={0}
            onClick={() => !disabled && fileInputRef.current?.click()}
            onKeyDown={(e) => !disabled && (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
            className={baseClass}
          >
            <Upload className="w-8 h-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">{placeholder}</span>
          </div>
          {allowUrlInput && !disabled && (
            <button
              type="button"
              onClick={() => setShowUrlInput((s) => !s)}
              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              <Link className="w-3.5 h-3.5" />
              {showUrlInput ? 'Hide URL input' : 'Or paste image URL'}
            </button>
          )}
        </div>
      )}

      {/* URL input panel */}
      {allowUrlInput && showUrlInput && !disabled && (
        <div className="flex gap-2 items-center">
          <input
            type="url"
            value={urlInputValue}
            onChange={(e) => { setUrlInputValue(e.target.value); setUrlError(null); }}
            placeholder="https://example.com/logo.png"
            className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${urlError ? 'border-red-400' : 'border-gray-300'}`}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlApply())}
          />
          <button
            type="button"
            onClick={handleUrlApply}
            disabled={isValidatingUrl}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isValidatingUrl ? 'Checking...' : 'Apply'}
          </button>
          <button
            type="button"
            onClick={() => { setShowUrlInput(false); setUrlInputValue(''); setUrlError(null); }}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* URL validation error */}
      {urlError && (
        <p className="text-sm text-red-600 flex items-center gap-1.5">
          <span>⚠</span> {urlError}
        </p>
      )}

      {/* Crop Modal */}
      {imgSrc && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Crop image</h3>
              <button
                type="button"
                onClick={handleCancelCrop}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Shape selector toolbar */}
            <div className="px-4 pt-3 pb-0 flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">Shape</span>
              <button
                type="button"
                onClick={() => handleShapeChange('rect')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                  currentShape === 'rect'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400 hover:text-emerald-600'
                }`}
              >
                <Square className="w-3.5 h-3.5" />
                Rectangle
              </button>
              <button
                type="button"
                onClick={() => handleShapeChange('round')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                  currentShape === 'round'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400 hover:text-emerald-600'
                }`}
              >
                <Circle className="w-3.5 h-3.5" />
                Circle
              </button>
            </div>

            <div className="p-4 overflow-auto flex-1">
              <p className="text-xs text-gray-400 mb-3">
                The white border is part of the canvas — drag the handles into the white area to include white space in your output.
                {currentShape === 'round' && ' Circle mode locks to 1:1 ratio.'}
              </p>
              <div className="overflow-auto bg-white rounded-lg flex items-center justify-center border border-gray-200" style={{ minHeight: '200px', maxHeight: '420px' }}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={reactCropAspect}
                  circularCrop={currentShape === 'round'}
                  className="max-w-full"
                >
                  <img
                    ref={imgRef}
                    src={imgSrc}
                    alt="Crop"
                    onLoad={onImageLoad}
                    style={
                      imgDimensions
                        ? (() => {
                            const scale = Math.min(560 / imgDimensions.w, 400 / imgDimensions.h, 1);
                            return {
                              width: imgDimensions.w * scale,
                              height: imgDimensions.h * scale,
                              maxWidth: 'none',
                              display: 'block',
                            };
                          })()
                        : { display: 'block' }
                    }
                  />
                </ReactCrop>
              </div>

              {cropError && (
                <p className="mt-2 text-sm text-red-600">{cropError}</p>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyCrop}
                disabled={isUploading}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? 'Uploading...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
